import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import Modal, { Field } from '../../common/Modal';
import { employeesApi, organizationApi } from '../../../services/api';
import { useAlert } from '../../../contexts/AlertContext';
import { fromPickerValue } from '../../../utils/dateOnly';

const AddGradeModal = ({
  isOpen,
  onClose,
  employeeId,
  positionEntry,
  existingGradeIds = [],
  onSuccess
}) => {
  const { showAlert } = useAlert();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [grades, setGrades] = useState([]);

  const [formData, setFormData] = useState({
    gradeId: '',
    startDate: dayjs(),
    endDate: null
  });

  useEffect(() => {
    if (isOpen) {
      fetchGrades();
      setFormData({
        gradeId: '',
        startDate: dayjs(),
        endDate: null
      });
    }
  }, [isOpen]);

  const fetchGrades = async () => {
    try {
      const gradesData = await organizationApi.getAllGrades();
      setGrades(gradesData);
    } catch (error) {
      showAlert('Failed to load grades', 'error', error);
    }
  };

  // Filter out grades that are already assigned to this position
  const availableGrades = grades.filter(
    grade => !existingGradeIds.includes(grade.id)
  );

  const handleSubmit = async () => {
    if (!formData.gradeId || !formData.startDate) {
      showAlert('Please fill in all required fields', 'error');
      return;
    }

    try {
      setIsSubmitting(true);

      const payload = {
        gradeId: formData.gradeId,
        positionHistoryId: positionEntry.id,
        startDate: fromPickerValue(formData.startDate)
      };

      if (formData.endDate) {
        payload.endDate = fromPickerValue(formData.endDate);
      }

      await employeesApi.addGradeHistory(employeeId, payload);
      showAlert('Grade added successfully', 'success');
      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Error adding grade:', error);
      showAlert(error.message || 'Failed to add grade', 'error', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({ gradeId: '', startDate: dayjs(), endDate: null });
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={`Add Grade to ${positionEntry?.position?.label || 'Position'}`}
      onSubmit={handleSubmit}
      submitText={isSubmitting ? 'Adding...' : 'Add Grade'}
      submitDisabled={!formData.gradeId || !formData.startDate || isSubmitting}
      width="450px"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, py: 1 }}>
        <Field>
          <FormControl fullWidth>
            <InputLabel>Grade</InputLabel>
            <Select
              value={formData.gradeId}
              onChange={(e) => setFormData(prev => ({ ...prev, gradeId: e.target.value }))}
              label="Grade"
            >
              {availableGrades.map(grade => (
                <MenuItem key={grade.id} value={grade.id}>
                  {grade.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {availableGrades.length === 0 && grades.length > 0 && (
            <Typography variant="caption" sx={{ color: '#DC2626', mt: 0.5 }}>
              All grades are already assigned to this position
            </Typography>
          )}
        </Field>

        <Field>
          <DatePicker
            label="Start Date"
            value={formData.startDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, startDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, startDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
        </Field>

        <Field>
          <DatePicker
            label="End Date (optional)"
            value={formData.endDate}
            onChange={(newValue) => {
              if (newValue && typeof newValue.isValid === 'function' && newValue.isValid()) {
                setFormData(prev => ({ ...prev, endDate: newValue }));
              } else if (newValue === null) {
                setFormData(prev => ({ ...prev, endDate: null }));
              }
            }}
            format="DD/MM/YYYY"
            disabled={isSubmitting}
            slotProps={{
              textField: {
                fullWidth: true,
                inputProps: { placeholder: 'DD/MM/YYYY' }
              }
            }}
          />
          <Typography variant="caption" sx={{ color: '#64748B', mt: 0.5 }}>
            Leave empty if this is the current grade
          </Typography>
        </Field>
      </Box>
    </Modal>
  );
};

export default AddGradeModal;
